import { Component, OnInit, Input, OnDestroy, HostBinding, ViewChild, ElementRef, Inject } from '@angular/core';

import {
  ILocation,
  ILane,
  LocationsService,
  LotModel,
  IStreamSourceType,
  AxxonService,
  IRTSPTransportType,
} from '@libs/portal-common/services';

import { AppConfigurationService, APP_CONFIGURATION } from '../../../../services/app-configuration.service';
import { catchError, map } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import { generateCustomGuid } from '@libs/portal-common';

const NOT_ALLOWED_IMAGE_PATH = '/img/skype/placeholders/camera-preview-not-available.jpg';

interface ILaneVideoSourceView {
  StreamSource: IStreamSourceType;
  SourceUrl?: string;
  IsPrimary?: string;
  Selected?: boolean;
  ProviderType?: string;
  ProviderName?: string;
  Code?: string;
  UserName?: string;
  Password?: string;
  ProviderIP?: string;
  Guid?: string;
  RtspTransport?: IRTSPTransportType;
  RtspAddress?: string;
  RtspUsername?: string;
  RtspPassword?: string;
  DialNumber?: number;
  DVR_Channel?: string;
  CameraId?: string | number;
}

@Component({
  selector: 'app-dvr-video',
  templateUrl: './dvr-video.component.html',
  styleUrls: ['./dvr-video.component.scss'],
})
export class DvrVideoComponent implements OnInit, OnDestroy {
  @HostBinding('class') class = 'info-tab-content';

  private _location: ILocation = null;
  private _lane: ILane = null;
  private dvrProxyUrl: string;

  warningMessage = 'Video is not available for this lane.';
  videoCameraSources: ILaneVideoSourceView[] = [];
  selectedVideoCameraSource: ILaneVideoSourceView = null;

  @Input('location')
  set location(location: ILocation) {
    this._location = location;
    this.initPlayer();
  }

  @Input('lane')
  set lane(lane: ILane) {
    this._lane = lane;
    this.initPlayer();
  }

  constructor(
    @Inject(APP_CONFIGURATION) private configuration: AppConfigurationService,
    private locations: LocationsService,
    private axxonService: AxxonService,
  ) {
    let dvrProxy = configuration.data.paths.dvrProxy;
    dvrProxy = dvrProxy.trimRight();
    dvrProxy = dvrProxy.endsWith('/') ? dvrProxy.slice(0, -1) : dvrProxy;
    this.dvrProxyUrl = `${dvrProxy}/camera`;
  }

  ngOnInit() {}

  ngOnDestroy() {
    this.destroyImage();
  }

  private initPlayer() {
    if (!this._location || !this._lane) {
      this.destroyImage();
      return;
    }

    this.locations.getBy(this._location.Id).subscribe((res) => {
      this.buildUrl(res.Lot, this._lane);
    });
  }

  private buildUrl(lot: LotModel, lane: ILane) {
    this.destroyImage();

    const sourceObservables = JSON.parse(lane.Video_Settings).map((vs: ILaneVideoSourceView) => {
      let cameraUrl: string = null;

      if (vs.StreamSource === IStreamSourceType.Axxon && vs.CameraId) {
        return this.getLiveStreamUrl(vs.CameraId.toString(), lot.AxxonDomainId).pipe(
          map(url => {
            return { ...vs, Guid: generateCustomGuid(), SourceUrl: url };
          })
        );
      } else if (vs.StreamSource === IStreamSourceType.Dvr) {
        if (!lot || !lane || !lot.DVRWebIP || !lot.DVRUserName || !lot.DVRPwd) {
          return of({ ...vs, Guid: generateCustomGuid(), SourceUrl: '' });
        }

        let notAllowed = false;
        let port = lot.DVRWebPort || 37777;
        let channel = !!vs.DVR_Channel ? vs.DVR_Channel : lane.Lane_Number ? lane.Lane_Number.toString() : notAllowed = true;

        cameraUrl = `dahua://${lot.DVRUserName}:${lot.DVRPwd}@${lot.DVRWebIP}:${port}/cam/realmonitor?channel=${channel}&subtype=1`;

        return of({ ...vs, Guid: generateCustomGuid(),
          SourceUrl: notAllowed ? NOT_ALLOWED_IMAGE_PATH : `${this.dvrProxyUrl}?url=${cameraUrl}` });
      } else if (vs.StreamSource === IStreamSourceType.Rtsp) {
        let address = vs.RtspAddress ? vs.RtspAddress.replace('rtsp://', '') : '';
        cameraUrl = `rtsp://${vs.RtspUsername}:${vs.RtspPassword}@${address}`;

        return of({ ...vs, Guid: generateCustomGuid(),
          SourceUrl: `${this.dvrProxyUrl}?url=${cameraUrl}` });
      } else {
        const finalObject = { ...vs, Guid: generateCustomGuid(), SourceUrl: '' };
        return of(finalObject);
      }
    });

    setTimeout(() => {
      if (sourceObservables.length > 0) {
        forkJoin(sourceObservables).subscribe((results: any) => {
          this.videoCameraSources = results;
          if (this.videoCameraSources.length > 0) {
            this.selectedVideoCameraSource = this.videoCameraSources.find((x) => x.IsPrimary);
            if (!this.selectedVideoCameraSource) {
              this.selectedVideoCameraSource = this.videoCameraSources[0];
            }
          }
          this.selectedVideoCameraSource.Selected = true;
          console.log('All video sources are loaded:', this.videoCameraSources);
        });
      } else {
        this.warningMessage = 'Video is not available for this lane.';
        this.videoCameraSources = [];
      }
    });

  }

  destroyImage() {
    this.warningMessage = '';
    this.selectedVideoCameraSource = null;
    this.videoCameraSources = [];
  }

  getLiveStreamUrl(cameraId: string, domainId: number): Observable<any> {
    return this.axxonService.getAxxonCameraLive(cameraId, domainId).pipe(
      map(resp => resp.FeedUrl ? resp.FeedUrl : NOT_ALLOWED_IMAGE_PATH),
      catchError((er): any => {
        console.error('Error while getting a stream url:', er);
        return new Error('Error while getting a stream url.');
      })
    );
  }

  selectCamera(source: ILaneVideoSourceView) {
    let previousCamera = this.videoCameraSources.find(s => s.Guid === this.selectedVideoCameraSource?.Guid);
    if (previousCamera) { previousCamera.Selected = false; }
    source.Selected = true;
    this.selectedVideoCameraSource = source;
  }

  updateErrorSourceUrl(event: any) {
    (event.target as HTMLImageElement).src = NOT_ALLOWED_IMAGE_PATH;
  }
}

